<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { ref, watch } from 'vue';
import { VBtn } from 'vuetify/components';
import BottleneckModal from './BottleneckModal.vue';
import BottleneckSolveForm, {
    type BottleneckSolveFormValues,
} from './BottleneckSolveForm.vue';
import { useMutation } from '@vue/apollo-composable';
import { graphql } from '@/gql';
import { useNotificationStore } from '@/stores/notification';
import type { NonNullableFields } from '@/utils';
import { BottleneckStatusType } from '@/gql/graphql';

const showDialog = defineModel<boolean>({ default: false });

const props = defineProps<{
    bottleneck: {
        id: string;
        name: string;
        description: string;
        responsible: string;
        solution: string | null;
        resolutionDate: string | null;
        escalations?: {
            escalationReason: string;
            expectedSolution: string | null;
        }[];
    };
    solvingEscalation?: boolean;
}>();

const emit = defineEmits<{
    confirm: [];
    cancel: [];
}>();

const { t } = useI18n({
    messages: {
        nl: {
            title: 'Bottleneck oplossen',
            title_escalation: 'Escalatie oplossen',
            confirm_text: 'Wil je de bottleneck ook oplossen?',
            yes: 'Ja, bottleneck ook oplossen',
            no: 'Nee, bottleneck niet oplossen',
            update_success: 'Bottleneck opgelost',
            deescalate_success: 'Escalatie opgelost',
        },
        en: {
            title: 'Solve bottleneck',
            title_escalation: 'Solve escalation',
            confirm_text: 'Do you also want to solve the bottleneck?',
            yes: 'Yes',
            no: 'No',
            update_success: 'Bottleneck successfully solved',
            deescalate_success: 'Escalation resolved',
        },
    },
});

const notificationStore = useNotificationStore();

const step = ref<'confirm' | 'solve'>('solve');
const pendingSolution = ref<string | null>(null);

watch(showDialog, (val) => {
    if (val) {
        step.value = 'solve';
        pendingSolution.value = null;
    }
});

const { mutate: updateBottleneckStatus, loading: isUpdating } = useMutation(
    graphql(`
        mutation UpdateBottleneckStatus(
            $statusInput: UpdateBottleneckStatusInput!
            $solutionInput: UpdateBottleneckInput!
        ) {
            updateBottleneck(input: $solutionInput) {
                result
            }
            updateBottleneckStatus(input: $statusInput) {
                ok
            }
        }
    `),
    {
        refetchQueries: [
            'Deliverable',
            'DeliverableBottlenecks',
            'ProjectPhases',
            'Escalations',
            'ProgramProjects',
        ],
        awaitRefetchQueries: true,
    }
);

const { mutate: updateEscalation } = useMutation(
    graphql(`
        mutation SetEscalationFinalSolution($input: UpdateEscalationInput!) {
            updateEscalation(input: $input) {
                result
            }
        }
    `)
);

const { mutate: deEscalate, loading: isDeEscalating } = useMutation(
    graphql(`
        mutation DeEscalateBottleneck($input: UpdateBottleneckStatusInput!) {
            updateBottleneckStatus(input: $input) {
                ok
            }
        }
    `),
    {
        refetchQueries: [
            'Deliverable',
            'DeliverableBottlenecks',
            'ProjectPhases',
            'Escalations',
            'ProgramProjects',
        ],
        awaitRefetchQueries: true,
    }
);

async function handleUpdate({
    solution,
}: NonNullableFields<BottleneckSolveFormValues>) {
    if (props.solvingEscalation) {
        pendingSolution.value = solution;
        step.value = 'confirm';
        return;
    }

    try {
        await updateBottleneckStatus({
            statusInput: {
                bottleneckId: props.bottleneck.id,
                status: BottleneckStatusType.SOLVED,
            },
            solutionInput: {
                bottleneckId: props.bottleneck.id,
                solution,
                name: props.bottleneck.name,
                description: props.bottleneck.description,
                responsible: props.bottleneck.responsible,
                resolutionDate: props.bottleneck.resolutionDate,
            },
        });

        showDialog.value = false;
        notificationStore.add({
            type: 'success',
            message: t('update_success'),
        });
        emit('confirm');
    } catch {
        notificationStore.add({ type: 'error', message: t('update_error') });
    }
}

async function handleSolveWithBottleneck() {
    try {
        await updateEscalation({
            input: {
                bottleneckId: props.bottleneck.id,
                finalSolution: pendingSolution.value,
                solution:
                    props.bottleneck.escalations?.[0]?.expectedSolution ?? '',
                escalationReason:
                    props.bottleneck.escalations?.[0]?.escalationReason ?? '',
            },
        });
        await updateBottleneckStatus({
            statusInput: {
                bottleneckId: props.bottleneck.id,
                status: BottleneckStatusType.SOLVED,
            },
            solutionInput: {
                bottleneckId: props.bottleneck.id,
                solution: pendingSolution.value!,
                name: props.bottleneck.name,
                description: props.bottleneck.description,
                responsible: props.bottleneck.responsible,
                resolutionDate: props.bottleneck.resolutionDate,
            },
        });

        showDialog.value = false;
        notificationStore.add({
            type: 'success',
            message: t('update_success'),
        });
        emit('confirm');
    } catch {
        notificationStore.add({ type: 'error', message: t('update_error') });
    }
}

async function handleDeEscalate() {
    try {
        await updateEscalation({
            input: {
                bottleneckId: props.bottleneck.id,
                finalSolution: pendingSolution.value,
                solution:
                    props.bottleneck.escalations?.[0]?.expectedSolution ?? '',
                escalationReason:
                    props.bottleneck.escalations?.[0]?.escalationReason ?? '',
            },
        });
        await deEscalate({
            input: {
                bottleneckId: props.bottleneck.id,
                status: BottleneckStatusType.BOTTLENECK,
            },
        });
        notificationStore.add({
            type: 'success',
            message: t('deescalate_success'),
        });
        showDialog.value = false;
        emit('confirm');
    } catch {
        notificationStore.add({ type: 'error', message: t('update_error') });
    }
}

function handleCancel() {
    showDialog.value = false;
    emit('cancel');
}
</script>

<template>
    <BottleneckModal
        v-model="showDialog"
        :title="solvingEscalation ? t('title_escalation') : t('title')"
    >
        <template #activator="{ props: activatorProps }">
            <slot name="activator" :props="activatorProps" />
        </template>
        <template v-if="step === 'confirm'">
            <p class="text-body-medium mb-6">{{ t('confirm_text') }}</p>
            <div class="d-flex justify-end ga-3">
                <VBtn variant="text" @click="handleCancel">{{
                    t('cancel')
                }}</VBtn>
                <VBtn
                    variant="text"
                    :loading="isDeEscalating"
                    @click="handleDeEscalate"
                >
                    {{ t('no') }}
                </VBtn>
                <VBtn
                    color="primary"
                    :loading="isUpdating"
                    @click="handleSolveWithBottleneck"
                >
                    {{ t('yes') }}
                </VBtn>
            </div>
        </template>
        <BottleneckSolveForm
            v-else
            :name="props.bottleneck.name"
            :initial-values="{ solution: solvingEscalation ? (props.bottleneck.escalations?.[0]?.expectedSolution ?? null) : props.bottleneck.solution }"
            :disabled="isUpdating"
            :loading="isUpdating"
            :solving-escalation
            @submit="handleUpdate"
            @cancel="handleCancel"
        />
    </BottleneckModal>
</template>
